import React, { useEffect, useState } from 'react';
import {
  TextField,
  Box,
  Typography,
  Alert,
  Button,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Collapse,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as PatentIcon,
  Article as PaperIcon,
  ShoppingCart as ProductIcon,
  Language as WebsiteIcon,
  MoreHoriz as OtherIcon,
} from '@mui/icons-material';

interface PriorArtEntry {
  id: string;
  title: string;
  type: string;
  source: string;
  differences?: string;
}

interface Props {
  data: any;
  onChange: (data: any) => void;
  onComplete: (completed: boolean) => void;
}

const PriorArt: React.FC<Props> = ({ data, onChange, onComplete }) => {
  const [entries, setEntries] = useState<PriorArtEntry[]>(
    data.priorArtEntries || [{ id: Date.now().toString(), title: '', type: '', source: '', differences: '' }]
  );
  const [publiclyDisclosed, setPubliclyDisclosed] = useState<string>(data.publiclyDisclosed || '');
  const [disclosureDetails, setDisclosureDetails] = useState<string>(data.disclosureDetails || '');
  const [expandedCards, setExpandedCards] = useState<{ [key: string]: boolean }>({});

  const priorArtTypes = [
    { value: 'patent', label: 'Patent', icon: <PatentIcon /> },
    { value: 'paper', label: 'Research Paper/Publication', icon: <PaperIcon /> },
    { value: 'product', label: 'Commercial Product', icon: <ProductIcon /> },
    { value: 'website', label: 'Website/Online Resource', icon: <WebsiteIcon /> },
    { value: 'conference', label: 'Conference Presentation', icon: <PaperIcon /> },
    { value: 'other', label: 'Other', icon: <OtherIcon /> },
  ];

  const getTypeIcon = (type: string) => {
    const typeObj = priorArtTypes.find((t) => t.value === type);
    return typeObj?.icon || <OtherIcon />;
  };

  const getTypeLabel = (type: string) => {
    const typeObj = priorArtTypes.find((t) => t.value === type);
    return typeObj?.label || type;
  };

  useEffect(() => {
    // Update parent component with data
    const newData = {
      ...data,
      priorArtEntries: entries,
      publiclyDisclosed,
      disclosureDetails,
    };
    onChange(newData);

    // Check if section is complete - at least need to answer disclosure question
    const disclosureComplete = publiclyDisclosed === 'no' || (publiclyDisclosed === 'yes' && disclosureDetails.trim().length > 0);
    const priorArtComplete = entries.some(
      (entry) => entry.title.trim() && entry.type && entry.source.trim()
    );

    const isComplete = disclosureComplete && priorArtComplete;
    onComplete(isComplete);
  }, [entries, publiclyDisclosed, disclosureDetails]);

  const handleAddEntry = () => {
    const newEntry: PriorArtEntry = {
      id: Date.now().toString(),
      title: '',
      type: '',
      source: '',
      differences: '',
    };
    setEntries([...entries, newEntry]);
    setExpandedCards({ ...expandedCards, [newEntry.id]: true });
  };

  const handleRemoveEntry = (id: string) => {
    setEntries(entries.filter((entry) => entry.id !== id));
    const newExpanded = { ...expandedCards };
    delete newExpanded[id];
    setExpandedCards(newExpanded);
  };

  const handleUpdateEntry = (id: string, field: keyof PriorArtEntry, value: string) => {
    setEntries(
      entries.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  const handlePublicDisclosureChange = (value: string) => {
    setPubliclyDisclosed(value);
    if (value === 'no') {
      setDisclosureDetails('');
    }
  };

  const toggleCardExpansion = (id: string) => {
    setExpandedCards({ ...expandedCards, [id]: !expandedCards[id] });
  };

  const isEntryComplete = (entry: PriorArtEntry) => {
    return entry.title.trim() && entry.type && entry.source.trim();
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Prior Art & Existing Solutions
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          List any similar inventions, products, patents, publications, or technologies you're aware of. This helps establish what makes your invention novel and is important for patent analysis.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Guidelines for Patent-Ready Description:
        </Typography>

        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Prior Art Entries:</strong> Be thorough – listing prior art demonstrates you understand the field and strengthens your patent application. Include patents (with number and country, e.g., "US Patent 1234567"), commercial products, research papers (with DOI or publication details), and any similar technologies. For each entry, explain the key technical differences from your invention. It's better to list something and explain differences than to omit it.
        </Typography>

        <Typography variant="body2">
          <strong>Personal Disclosure:</strong> Public disclosure before filing can affect patentability. Most countries require filing within 12 months of any public presentation, publication, or demonstration. Be honest and specific about dates and venues – this information is critical for determining your filing strategy and protecting your patent rights.
        </Typography>
      </Alert>

      {/* Personal Disclosure Section */}
      <Box sx={{ mb: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
            Personal Disclosure: Have you publicly disclosed this invention? *
          </FormLabel>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            (e.g., displayed, reported, published, or presented at conferences, oral or poster presentations)
          </Typography>
          <RadioGroup
            value={publiclyDisclosed}
            onChange={(e) => handlePublicDisclosureChange(e.target.value)}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        {publiclyDisclosed === 'yes' && (
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Full Description of Disclosure"
            value={disclosureDetails}
            onChange={(e) => setDisclosureDetails(e.target.value)}
            margin="normal"
            required
            helperText="Include name of event/publication, place, and date"
            placeholder="e.g., Presented at IEEE Conference 2024, New York, March 15-17, 2024"
          />
        )}
      </Box>

      {/* Prior Art Entries */}
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Prior Art & Existing Solutions
        </Typography>
          {entries.map((entry, index) => {
            const isExpanded = expandedCards[entry.id] !== false; // Default to expanded
            const isComplete = isEntryComplete(entry);

            return (
              <Card
                key={entry.id}
                sx={{
                  mb: 2,
                  border: isComplete ? '1px solid #4caf50' : '1px solid #e0e0e0',
                  boxShadow: isComplete ? '0 2px 4px rgba(76, 175, 80, 0.2)' : 1,
                }}
              >
                <CardContent>
                  {/* Card Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      {entry.type && getTypeIcon(entry.type)}
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                        Prior Art Entry #{index + 1}
                      </Typography>
                      {isComplete && (
                        <Chip label="Complete" color="success" size="small" />
                      )}
                      {!isExpanded && entry.title && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 1, fontStyle: 'italic' }}
                          noWrap
                        >
                          - {entry.title}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => toggleCardExpansion(entry.id)}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      {entries.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveEntry(entry.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {/* Card Content - Collapsible */}
                  <Collapse in={isExpanded}>
                    <Box>
                      <TextField
                        fullWidth
                        required
                        label="Title/Name"
                        value={entry.title}
                        onChange={(e) => handleUpdateEntry(entry.id, 'title', e.target.value)}
                        margin="normal"
                        placeholder="e.g., Smart Thermostat System, Patent US123456"
                        helperText="Short descriptive title or identifier"
                        inputProps={{ maxLength: 200 }}
                      />

                      <TextField
                        fullWidth
                        required
                        select
                        label="Type"
                        value={entry.type}
                        onChange={(e) => handleUpdateEntry(entry.id, 'type', e.target.value)}
                        margin="normal"
                        helperText="Select the type of prior art"
                      >
                        {priorArtTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {type.icon}
                              {type.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        fullWidth
                        required
                        label="Source/Reference/Link"
                        value={entry.source}
                        onChange={(e) => handleUpdateEntry(entry.id, 'source', e.target.value)}
                        margin="normal"
                        placeholder="Patent number, URL, DOI, ISBN, or detailed reference"
                        helperText="Provide patent number, URL, DOI, or enough detail for verification"
                        inputProps={{ maxLength: 500 }}
                      />

                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Key Differences (Optional)"
                        value={entry.differences || ''}
                        onChange={(e) => handleUpdateEntry(entry.id, 'differences', e.target.value)}
                        margin="normal"
                        placeholder="Briefly explain how your invention differs from this"
                        helperText="This helps establish novelty - optional but recommended"
                        inputProps={{ maxLength: 500 }}
                      />
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}

        {/* Add Another Button */}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddEntry}
          sx={{ mb: 3 }}
        >
          Add Another Prior Art Entry
        </Button>
      </Box>

    </Box>
  );
};

export default PriorArt;
